import { useState, useEffect, useRef, useCallback, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import styles from './HierarchicalSelect.module.css';
import { AttributeSelectModal } from './AttributeSelectModal';
import { ChevronIcon, ExpandIcon } from './selectIcons';
import { Tooltip } from './Tooltip';

export type HierarchicalOption = {
  id: string;
  key: string;
  label: string;
  type: string;
  is_sensitive?: boolean;
  metadata?: Record<string, unknown> | null;
  children?: HierarchicalOption[];
};

/**
 * 商品AIプロファイル推奨 (計画書§5.2)。strong/suggested に含まれる option key を
 * 上位ソート＋「おすすめ」バッジ表示の対象にする。既定選択(自動選択)には使わない。
 */
export type RecommendedKeys = {
  strong: string[];
  suggested: string[];
};

/** options に「子を持つ group」が含まれるか（ネスト判定）。true ならモーダル表示にする。 */
function optionsHaveNestedGroups(options: HierarchicalOption[]): boolean {
  return options.some((opt) => Array.isArray(opt.children) && opt.children.length > 0);
}

function recommendedRank(key: string, recommendedKeys: RecommendedKeys): number {
  if (recommendedKeys.strong.includes(key)) return 0;
  if (recommendedKeys.suggested.includes(key)) return 1;
  return 2;
}

/** strong→suggested→その他の順に安定ソート（同一カテゴリ内のみ。グループ構造は壊さない） */
export function sortByRecommended<T extends { key: string }>(
  items: T[],
  recommendedKeys?: RecommendedKeys,
): T[] {
  if (!recommendedKeys || (recommendedKeys.strong.length === 0 && recommendedKeys.suggested.length === 0)) {
    return items;
  }
  return [...items].sort((a, b) => recommendedRank(a.key, recommendedKeys) - recommendedRank(b.key, recommendedKeys));
}

export function isRecommendedKey(key: string, recommendedKeys?: RecommendedKeys): boolean {
  if (!recommendedKeys) return false;
  return recommendedKeys.strong.includes(key) || recommendedKeys.suggested.includes(key);
}

interface HierarchicalSelectProps {
  label: string;
  /** ラベルの前に表示するアイコン (public 配下の svg URL)。mask でラベル色に合わせて描画。 */
  icon?: string;
  /** アイコンの回転角(度)。例: ボトルを横にする場合 90。 */
  iconRotate?: number;
  options: HierarchicalOption[];
  value: string;
  onChange: (selectedKey: string) => void;
  /** リセット/未選択表示のラベル(既定「指定なし」)。映像スタイルは「デフォルト」。 */
  noneLabel?: string;
  /** この値を「既定(=変更なし)」とみなす。value===defaultValue なら枠線強調・リセットを出さない。
   *  既定 'none'(従来挙動)。映像スタイルは 'photorealistic' を渡す。 */
  defaultValue?: string;
  /** 商品AIプロファイル推奨 (計画書§5.2)。上位ソート＋バッジのみ。既定選択はしない。 */
  recommendedKeys?: RecommendedKeys;
  /** 選択肢ごとにラベルの頭へ出すアイコン (public 配下の svg URL)。無ければ undefined。
   *  ドロップダウン / モーダルの選択肢と、選択中のトリガー表示に出す。 */
  optionIcon?: (option: HierarchicalOption) => string | undefined;
  /** 選択肢ごとのツールチップ (短縮名のラベルに対する正式名など)。ドロップダウン表示の選択肢とトリガーに出す。 */
  optionTooltip?: (option: HierarchicalOption) => string | undefined;
  /** plain = 背景色・枠線なしの小さな「値 + シェブロン」だけのトリガーで、ラベル (小さめ・細め) と
   *  同じ行に「ラベル → 値 → リセット ✕」の順で並べる (動画エディタの EditorSelect variant="plain" と
   *  同じ見た目。思考レベルなどモデル固有の補助設定向け)。既定 'bordered' は従来の枠付きトリガー + ラベル上段。 */
  variant?: 'bordered' | 'plain';
  /** true なら選択肢が平坦 (入れ子 group 無し) でもアンカードロップダウンではなく全画面モーダルで開く。
   *  属性ノードの metadata.display='modal' (体格・身体的特徴 / ファッション) から渡される。
   *  パートナーの非表示設定でサブ group が消えても見た目が変わらないようにするためのもの。 */
  forceModal?: boolean;
}

/** 選択肢ラベルの頭に置くアイコン (mask でラベル色に合わせて描画)。 */
export function OptionIcon({ url, className }: { url: string; className?: string }) {
  return (
    <span
      className={`${styles.optionIcon} ${className ?? ''}`}
      style={{ maskImage: `url(${url})`, WebkitMaskImage: `url(${url})` }}
      aria-hidden="true"
    />
  );
}

/** 選択値の option を再帰的に探す */
function findOption(options: HierarchicalOption[], targetKey: string): HierarchicalOption | null {
  for (const opt of options) {
    if (opt.key === targetKey) return opt;
    if (opt.children) {
      const found = findOption(opt.children, targetKey);
      if (found) return found;
    }
  }
  return null;
}

/** 選択値のラベルを再帰的に探す（子のラベルのみ返す） */
function findDisplayLabel(options: HierarchicalOption[], targetKey: string): string | null {
  for (const opt of options) {
    if (opt.key === targetKey) return opt.label;
    if (opt.children) {
      const found = findDisplayLabel(opt.children, targetKey);
      if (found) return found;
    }
  }
  return null;
}

/** 選択値までのグループキーのパスを返す */
function findPathToValue(items: HierarchicalOption[], targetKey: string): string[] | null {
  for (const item of items) {
    if (item.key === targetKey) return [];
    if (item.children) {
      const path = findPathToValue(item.children, targetKey);
      if (path !== null) return [item.key, ...path];
    }
  }
  return null;
}

export function HierarchicalSelect({ label, icon, iconRotate, options, value, onChange, noneLabel = '指定なし', defaultValue = 'none', recommendedKeys, optionIcon, optionTooltip, variant = 'bordered', forceModal = false }: HierarchicalSelectProps) {
  const isPlain = variant === 'plain';
  const [isOpen, setIsOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 子を持つ group を含む（ネストする）場合は全画面モーダルで表示する。
  // 単純なフラット選択肢のみの場合は従来のアンカードロップダウン (forceModal で常にモーダル)。
  const useModal = forceModal || optionsHaveNestedGroups(options);

  const updateDropdownPosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, []);

  // Click-outside & scroll/resize listeners (ドロップダウン時のみ。モーダルは自前で処理)
  useEffect(() => {
    if (!isOpen || useModal) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (
        containerRef.current && !containerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    updateDropdownPosition();
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    window.addEventListener('scroll', updateDropdownPosition, true);
    window.addEventListener('resize', updateDropdownPosition);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      window.removeEventListener('scroll', updateDropdownPosition, true);
      window.removeEventListener('resize', updateDropdownPosition);
    };
  }, [isOpen, useModal, updateDropdownPosition]);

  // Escape key (ドロップダウン時のみ。モーダルは自前で処理)
  useEffect(() => {
    if (!isOpen || useModal) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, useModal]);


  const handleToggle = () => {
    if (useModal) {
      setIsOpen((prev) => !prev);
      return;
    }
    if (!isOpen) {
      // 選択中の値が含まれるグループを自動展開
      const pathKeys = findPathToValue(options, value);
      if (pathKeys && pathKeys.length > 0) {
        setExpandedGroups(new Set(pathKeys));
      } else {
        setExpandedGroups(new Set());
      }
      // 選択中のオプションを上下中央にスクロール
      requestAnimationFrame(() => {
        const dropdown = dropdownRef.current;
        if (!dropdown) return;
        const selected = dropdown.querySelector('[aria-selected="true"]') as HTMLElement | null;
        if (selected) {
          const dropdownHeight = dropdown.clientHeight;
          const selectedTop = selected.offsetTop;
          const selectedHeight = selected.offsetHeight;
          dropdown.scrollTop = selectedTop - dropdownHeight / 2 + selectedHeight / 2;
        }
      });
    }
    setIsOpen(!isOpen);
  };

  const handleSelect = (key: string) => {
    onChange(key);
    setIsOpen(false);
  };

  const toggleGroup = (key: string, headerElement?: HTMLElement) => {
    const isExpanding = !expandedGroups.has(key);
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
    // 展開時、子要素が見えるようにグループヘッダーまでスムーズスクロール
    if (isExpanding && headerElement && dropdownRef.current) {
      requestAnimationFrame(() => {
        const dropdown = dropdownRef.current;
        if (!dropdown) return;
        const headerRect = headerElement.getBoundingClientRect();
        const dropdownRect = dropdown.getBoundingClientRect();
        // ヘッダーがドロップダウンの下半分にある場合、スクロールして上に寄せる
        if (headerRect.top > dropdownRect.top + dropdownRect.height * 0.4) {
          const scrollOffset = headerRect.top - dropdownRect.top - 8;
          dropdown.scrollTo({ top: dropdown.scrollTop + scrollOffset, behavior: 'smooth' });
        }
      });
    }
  };

  const displayLabel = findDisplayLabel(options, value) ?? noneLabel;
  const selectedOption = optionIcon || optionTooltip ? findOption(options, value) : null;
  const displayIcon = selectedOption ? optionIcon?.(selectedOption) : undefined;
  const displayTooltip = selectedOption ? optionTooltip?.(selectedOption) : undefined;

  function renderOptions(items: HierarchicalOption[], depth: number): ReactNode {
    const sortedItems = sortByRecommended(items, recommendedKeys);
    return sortedItems.map((item) => {
      // リーフオプション（childrenがない、または空）
      if (!item.children || item.children.length === 0) {
        const tooltip = optionTooltip?.(item);
        const button = (
          <button
            key={item.id}
            type="button"
            role="option"
            aria-selected={value === item.key}
            className={`${styles.option} ${value === item.key ? styles.optionSelected : ''} ${item.is_sensitive ? styles.optionSensitive : ''}`}
            style={{ paddingLeft: `${12 + depth * 16}px` }}
            onClick={() => handleSelect(item.key)}
          >
            <span className={styles.optionLabel}>
              {(() => { const url = optionIcon?.(item); return url ? <OptionIcon url={url} /> : null; })()}
              {item.label}
            </span>
            {isRecommendedKey(item.key, recommendedKeys) && (
              <span className={styles.recommendedBadge}>おすすめ</span>
            )}
          </button>
        );
        return tooltip ? (
          <Tooltip key={item.id} content={tooltip} placement="bottom" className={styles.optionTooltipWrap}>{button}</Tooltip>
        ) : button;
      }

      // グループ（アコーディオン）
      const isExpanded = expandedGroups.has(item.key);
      return (
        <div key={item.id}>
          <button
            type="button"
            className={`${styles.groupHeader} ${item.is_sensitive ? styles.optionSensitive : ''}`}
            style={{ paddingLeft: `${12 + depth * 16}px` }}
            onClick={(e) => toggleGroup(item.key, e.currentTarget)}
            aria-expanded={isExpanded}
          >
            <span className={styles.groupChevron}>
              {isExpanded ? '\u25BE' : '\u25B8'}
            </span>
            <span>{item.label}</span>
          </button>
          {isExpanded && (
            <div className={styles.groupChildren}>
              {renderOptions(item.children, depth + 1)}
            </div>
          )}
        </div>
      );
    });
  }

  const hasValue = value && value !== 'none' && value !== defaultValue;
  // リセット ✕。bordered はラベル行の右端、plain はトリガーの右隣に置く。
  // 戻し先は defaultValue (既定 'none' なので従来どおり「指定なし」。思考レベルのように
  // 「指定なし」が存在しない設定は defaultValue=既定の選択肢を渡し、そこへ戻す)
  const clearButton = hasValue ? (
    <button
      type="button"
      className={styles.clearButton}
      onClick={() => { onChange(defaultValue); setIsOpen(false); }}
      aria-label="リセット"
      data-tooltip="リセット"
      data-tooltip-bottom=""
    >
      ✕
    </button>
  ) : null;

  const triggerButton = (
      <button
        ref={triggerRef}
        type="button"
        className={`${styles.trigger} ${isPlain ? styles.triggerPlain : ''} ${hasValue ? styles.triggerHasValue : ''}`}
        onClick={handleToggle}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {displayIcon && <OptionIcon url={displayIcon} />}
        <span className={styles.triggerText}>{displayLabel}</span>
        <span className={styles.triggerArrow}>{useModal ? <ExpandIcon /> : <ChevronIcon open={isOpen} />}</span>
      </button>
  );

  return (
    <div ref={containerRef} className={`${styles.container} ${isPlain ? styles.containerPlain : ''}`}>
      <div className={styles.labelRow}>
        <div className={styles.label}>
          {icon && (
            <span
              className={styles.labelIcon}
              style={{
                maskImage: `url(${icon})`,
                WebkitMaskImage: `url(${icon})`,
                transform: iconRotate ? `rotate(${iconRotate}deg)` : undefined,
              }}
              aria-hidden="true"
            />
          )}
          {label}
        </div>
        {!isPlain && clearButton}
      </div>
      {displayTooltip ? (
        <Tooltip content={displayTooltip} disabled={isOpen} className={styles.optionTooltipWrap}>{triggerButton}</Tooltip>
      ) : triggerButton}
      {isPlain && clearButton}
      {useModal ? (
        <AttributeSelectModal
          label={label}
          options={options}
          value={value}
          onChange={onChange}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          noneLabel={noneLabel}
          defaultValue={defaultValue}
          recommendedKeys={recommendedKeys}
          optionIcon={optionIcon}
        />
      ) : (
        isOpen && createPortal(
          <div
            ref={dropdownRef}
            className={`modal-surface ${styles.dropdown}`}
            style={{
              position: 'fixed',
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              minWidth: dropdownPosition.width,
            }}
            role="listbox"
          >
            {renderOptions(options, 0)}
          </div>,
          document.body
        )
      )}
    </div>
  );
}
